import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  afterNextRender,
  inject,
  signal,
  PLATFORM_ID
} from '@angular/core';
import { Subject } from 'rxjs';
import { NavigationOptions, SwiperOptions } from 'swiper/types';

import { Environment, ENVIRONMENT } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';

import { HTMLSwiperElement } from '../interfaces';

/**
 * Carousel component to extend and create a carousel of any
 * type of items. Check the things to override in the extended
 * component.
 */
@Component({
  selector: 'community-elements-feature-carousel',
  template: ``
})
export class FeatureCarouselComponent<T> implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  @Optional() public readonly channelService = inject(ChannelService);

  @Input() protected uniqueSelector = this.generateUniqueId();
  @Input() title: string;
  @Input() subtitle: string;
  @Input() orientation: 'portrait' | 'landscape' = 'portrait';
  @Input() params: unknown;
  @Input() isPlaceholder = false;
  @Input() autoplay?: boolean;
  @Input() loop?: boolean;
  @Input() set items(value: T[]) {
    this._items = value;
    this.updateNoItemsBinding();
  }
  get items(): T[] {
    return this._items;
  }

  _items?: T[];
  protected noItemsCarousel = false;

  @HostBinding('class.no-items-carousel')
  get noItems() {
    return this.noItemsCarousel;
  }

  swiperElement: HTMLSwiperElement;

  unsubscriber = new Subject();
  swiperConfig: SwiperOptions;
  channelSettings;
  arrowLeftCssSelector: string;
  arrowRightCssSelector: string;
  arrowTemplate: number = 7;
  readonly setSwiperSlideHeightAuto = this.env.setSwiperSlideHeightAuto ?? false;
  setCustomCarouselStyles = false;


  /**
   * Used to know if {@link retrieveItems} method has been called and completed.
   */
  isRetrievedItems = signal(false);

  constructor(
    @Inject(ENVIRONMENT) public env: Environment,
    @Inject(PLATFORM_ID) private platform
  ) {
    this.onConstructor();
  }

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platform);
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.beforeInitSwiper();
      this.initSwiper();
    }
  }

  /// FOR DEBUG PROPOSES ///

  // ngAfterViewInit(): void {
  //   this.findSwiperComponent();
  // }

  // private findSwiperComponent() {
  //   const thisEl = this.elementRef.nativeElement;
  //   this.swiperElement = thisEl.querySelector('swiper-container');
  // }

  ////////////////////////////

  /**
   * @description This method is called on Contructor.
   * Rewrite this method in the extended component.
   */
  protected onConstructor() {}

  /**
   * @description This method is called before the swiper is
   * initialized, it can be used to set up.
   * Rewrite this method in the extended component.
   */
  protected beforeInitSwiper() {
    this.updateNoItemsBinding();
  }

  protected initSwiper() {
    this.generateArrowSelectors();
    this.setUpArrowTemplate();
    this.channelSettings = this.channelService?.channel?.settings;
    this.swiperConfig = this.getSwiperConfig();

    // get movies from movie service with params
    if (!this.items) {
      this.retrieveItems().then(items => {
        this._items = items;
        this.updateNoItemsBinding();
        if (this.items.length === 0) {
          this.hideArrows();
        }
        this.isRetrievedItems.set(true);
      });
    }
  }

  protected getSwiperConfig(): SwiperOptions {
    const envConfig = this.env.carouselTypeParams?.collections?.swiperConfig;
    if(this.orientation === 'portrait' && this.setSwiperSlideHeightAuto){
      this.setCustomCarouselStyles = true;
    }
    const defaultConfig = {
      slidesPerView: 'auto' as 'auto' | number,
      slidesPerGroupAuto: !!this.env.homePageConfig?.enableGroupSlidingForCarousels,
      spaceBetween: 10,
      scrollbar: true,
      navigation: this.getNavigationSelectors(),
      breakpoints: {
        480: {
          spaceBetween: 30
        }
      },
      on: {
        init: () => {}
      },
      loop: this.loop,
      autoplay: this.autoplay ? { delay: 3000 } : false
    };
    if (envConfig) return { ...defaultConfig, ...envConfig };
    return defaultConfig;
  }

  protected getNavigationSelectors(): NavigationOptions | boolean {
    return {
      prevEl: `#${this.arrowLeftCssSelector}`,
      nextEl: `#${this.arrowRightCssSelector}`
    };
  }

  protected async retrieveItems(): Promise<T[]> {
    return [];
  }

  private hideArrows() {
    const arrowLeft = document.getElementById(this.arrowLeftCssSelector);
    const arrowRight = document.getElementById(this.arrowRightCssSelector);

    if (arrowLeft) {
      arrowLeft.style.display = 'none';
    }
    if (arrowRight) {
      arrowRight.style.display = 'none';
    }
  }

  private generateUniqueId() {
    return Math.random()
      .toString(36)
      .substring(2, 9 + 2);
  }

  protected generateArrowSelectors() {
    this.generateUniqueId();
    this.arrowLeftCssSelector = `arrow-left_${this.uniqueSelector}`;
    this.arrowRightCssSelector = `arrow-right_${this.uniqueSelector}`;
  }

  protected setUpArrowTemplate() {
    const arrowLayoutDesign =
      this.env.carouselTypeParams?.collections?.arrowLayoutDesing ||
      this.env.carouselTypeParams?.movies?.arrowLayoutDesing;
    this.arrowTemplate =
      arrowLayoutDesign?.top || arrowLayoutDesign?.down || arrowLayoutDesign?.side || arrowLayoutDesign?.inside;
  }

  protected updateNoItemsBinding() {
    const newValue = !this._items || this._items.length === 0;
    if (this.noItemsCarousel !== newValue) {
      this.noItemsCarousel = newValue;
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }
}
